import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Download, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { BackdateField } from "@/components/BackdateField";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TINT = {
  pending_payment_confirmation: "bg-amber-100 text-amber-800",
  payment_confirmed: "bg-blue-100 text-blue-800",
  active: "bg-emerald-100 text-emerald-800",
  expired: "bg-slate-200 text-slate-700",
  cancelled: "bg-rose-100 text-rose-700",
};

// Warranty & Protection Plan opt-in panel, shown on a Delivered job to the pipeline owner.
export const ProtectionPanel = ({ job }) => {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [optedIn, setOptedIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  const canOptIn = can(user, "protection:optin");

  const load = useCallback(() => {
    api.get(`/jobs/${job.id}/protection-plan`).then(({ data }) => {
      setPlan(data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [job.id]);

  useEffect(() => {
    load();
    const v = job.vehicle || {};
    setForm({
      customer_name: job.customer_name || "",
      contact: job.customer_phone || "",
      email: job.customer_email || "",
      mailing_address: "",
      vehicle_model: [v.make, v.model, v.colour].filter(Boolean).join(" "),
      registration_no: "",
      chassis_no: "",
      area_sqft_covered: job.ppf_sqft ? String(job.ppf_sqft) : "",
      body_parts_covered: "Full Body",
      installer_name: job.installer_name || "",
      invoice_date: "",
      payment_amount: job.price ? String(job.price) : "",
    });
  }, [job, load]);

  // Pull warranty details entered by the studio (email/chassis/registration may be saved per
  // product on the job) so the Protection Plan form pre-fills them instead of showing blank.
  useEffect(() => {
    if (!job.id) return;
    api.get(`/jobs/${job.id}/warranty-details`).then(({ data }) => {
      setForm((f) => ({
        ...f,
        email: f.email || data.customer_email || "",
        chassis_no: f.chassis_no || data.chassis_vin || "",
        registration_no: f.registration_no || data.registration_no || "",
        invoice_date: f.invoice_date || (data.invoice_date || "").slice(0, 10),
      }));
    }).catch(() => {});
  }, [job.id]);

  if (!canOptIn) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!optedIn) return;
    if (!form.customer_name?.trim()) return toast.error("Customer name is required");
    if (!(Number(form.area_sqft_covered) > 0)) return toast.error("Area (sq. ft.) covered is required");
    setBusy(true);
    try {
      const { data } = await api.post("/protection-plans", {
        job_id: job.id,
        ...form,
        area_sqft_covered: Number(form.area_sqft_covered),
        payment_amount: form.payment_amount === "" ? null : Number(form.payment_amount),
      });
      toast.success("Protection Plan submitted — sent to Accountant for payment confirmation");
      setPlan(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-4 border-brand-line shadow-none" data-testid="protection-panel">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Warranty &amp; Protection Plan</CardTitle>
        {plan && (
          <Badge className={`border-0 capitalize ${STATUS_TINT[plan.status] || "bg-slate-100"}`}
            data-testid="protection-status">
            {String(plan.status).replace(/_/g, " ")}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loaded && plan ? (
          <div className="space-y-2 text-sm" data-testid="protection-existing">
            {plan.status === "pending_payment_confirmation" && (
              <p className="flex items-center gap-2 text-amber-700">
                <Clock className="h-4 w-4" /> Opted in — awaiting Accountant payment confirmation.
                No certificate is generated until payment is confirmed.
              </p>
            )}
            {plan.status === "payment_confirmed" && (
              <p className="flex items-center gap-2 text-blue-700">
                <Clock className="h-4 w-4" /> Payment confirmed — awaiting CRM registration.
                The certificate is issued and emailed once CRM registers the plan.
              </p>
            )}
            {plan.status === "active" && (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Active · Certificate {plan.certificate_number} ·
                  valid until {plan.valid_until} · balance {plan.remaining_sqft_balance} sq. ft.
                </p>
                <Button size="sm" variant="outline" data-testid="protection-download-btn"
                  onClick={() => window.open(`${API}/protection-plans/${plan.id}/certificate`, "_blank")}>
                  <Download className="mr-2 h-4 w-4" /> Certificate
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={optedIn} onCheckedChange={(v) => setOptedIn(!!v)}
                data-testid="protection-optin-checkbox" />
              Customer has opted for the 2-Year Protection Plan
            </label>

            {optedIn && (
              <div className="space-y-3" data-testid="protection-form">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    ["customer_name", "Customer name *"],
                    ["contact", "Contact"],
                    ["email", "Email"],
                    ["mailing_address", "Mailing address"],
                    ["vehicle_model", "Vehicle model"],
                    ["registration_no", "Registration no."],
                    ["chassis_no", "Chassis no."],
                    ["installer_name", "Installer / team"],
                    ["body_parts_covered", "Body parts covered *"],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-xs">{label}</Label>
                      <Input className="mt-1" data-testid={`protection-${k}`} value={form[k] ?? ""}
                        onChange={(e) => set(k, e.target.value)} />
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs">Area / sq. ft. covered *</Label>
                    <Input className="mt-1" inputMode="decimal" data-testid="protection-area"
                      value={form.area_sqft_covered ?? ""}
                      onChange={(e) => set("area_sqft_covered", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Plan amount (optional)</Label>
                    <Input className="mt-1" inputMode="decimal" value={form.payment_amount ?? ""}
                      onChange={(e) => set("payment_amount", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Invoice date</Label>
                    <Input className="mt-1" type="date" data-testid="protection-invoice-date"
                      value={form.invoice_date ?? ""}
                      onChange={(e) => set("invoice_date", e.target.value)} />
                    <p className="mt-1 text-[11px] text-slate-400">Plan starts from this date.</p>
                  </div>
                </div>
                <BackdateField value={form.entry_date} onChange={(v) => set("entry_date", v)} />
                <Button data-testid="protection-submit-btn" disabled={busy || !optedIn}
                  onClick={submit}>
                  <ShieldCheck className="mr-2 h-4 w-4" /> Submit for payment confirmation
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
